import React, { useEffect, useState } from "react";
import { Loader2, Save } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { api } from "@/lib/api";
import { useSite } from "@/context/SiteContext";
import ImageUpload from "@/components/admin/ImageUpload";

function Field({ label, k, form, set, type = "text", placeholder, hint }) {
  return (
    <div>
      <Label>{label}</Label>
      <Input type={type} value={form[k] || ""} onChange={(e) => set(k, e.target.value)} className="bg-[var(--bg-secondary)]" placeholder={placeholder} data-testid={`settings-${k}`} />
      {hint && <p className="text-xs text-[var(--text-muted)] mt-1">{hint}</p>}
    </div>
  );
}

function Card({ title, desc, children }) {
  return (
    <div className="card-surface p-6 space-y-4">
      <div><h3 className="font-head font-bold text-lg">{title}</h3>{desc && <p className="text-xs text-[var(--text-muted)]">{desc}</p>}</div>
      {children}
    </div>
  );
}

export default function SettingsTab() {
  const { settings, refresh } = useSite();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { if (settings) setForm(settings); }, [settings]);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const save = async () => {
    setSaving(true);
    try {
      await api.put("/settings", form);
      await refresh();
      toast.success("Configurações salvas!");
    } catch { toast.error("Falha ao salvar"); }
    finally { setSaving(false); }
  };

  return (
    <div className="max-w-2xl space-y-6 pb-20">
      <Card title="Logos das marcas" desc="Substitua os logos exibidos no cabeçalho de cada marca.">
        <ImageUpload value={form.stamp_logo_url} onChange={(u) => set("stamp_logo_url", u)} label="Logo K8 Stamp" />
        <ImageUpload value={form.sports_logo_url} onChange={(u) => set("sports_logo_url", u)} label="Logo K8 Sports" />
      </Card>

      <Card title="Imagens principais (hero)">
        <ImageUpload value={form.stamp_hero_image} onChange={(u) => set("stamp_hero_image", u)} label="Hero K8 Stamp" />
        <ImageUpload value={form.sports_hero_image} onChange={(u) => set("sports_hero_image", u)} label="Hero K8 Sports" />
        <ImageUpload value={form.store_hero_image} onChange={(u) => set("store_hero_image", u)} label="Hero Loja do Evento" />
      </Card>

      <Card title="Contato" desc="Campos vazios ficam ocultos no site público.">
        <Field label="WhatsApp (único p/ as duas marcas)" k="whatsapp_number" form={form} set={set} placeholder="5581999999999" hint="Somente números, com DDI e DDD." />
        <Field label="E-mail de contato" k="contact_email" form={form} set={set} type="email" />
      </Card>

      <Card title="Redes sociais" desc="Botões ficam ocultos quando o link não é cadastrado.">
        <Field label="Instagram — K8 Stamp" k="stamp_instagram" form={form} set={set} placeholder="https://instagram.com/..." />
        <Field label="Facebook — K8 Stamp" k="stamp_facebook" form={form} set={set} />
        <Field label="Instagram — K8 Sports" k="sports_instagram" form={form} set={set} />
        <Field label="Facebook — K8 Sports" k="sports_facebook" form={form} set={set} />
      </Card>

      <Card title="Dados da empresa">
        <Field label="Nome da empresa" k="company_name" form={form} set={set} />
        <Field label="Endereço" k="company_address" form={form} set={set} />
        <Field label="Horário de atendimento" k="company_hours" form={form} set={set} />
      </Card>

      <Card title="Loja do evento — entrega e retirada">
        <div><Label>Informações de retirada</Label><Textarea value={form.store_pickup_info || ""} onChange={(e) => set("store_pickup_info", e.target.value)} className="bg-[var(--bg-secondary)]" data-testid="settings-store_pickup_info" /></div>
        <div><Label>Informações de entrega</Label><Textarea value={form.store_delivery_info || ""} onChange={(e) => set("store_delivery_info", e.target.value)} className="bg-[var(--bg-secondary)]" data-testid="settings-store_delivery_info" /></div>
        <Field label="Taxa fixa de entrega" k="store_delivery_fee" form={form} set={set} type="number" placeholder="0.00" hint="Valor somado automaticamente ao pedido quando o cliente escolhe entrega. Use 0 para combinar depois." />
      </Card>

      <Card title="Políticas" desc="Revise antes de publicar.">
        <div><Label>Política de privacidade</Label><Textarea value={form.policies_privacy || ""} onChange={(e) => set("policies_privacy", e.target.value)} className="bg-[var(--bg-secondary)]" rows={4} /></div>
        <div><Label>Condições de compra</Label><Textarea value={form.policies_purchase || ""} onChange={(e) => set("policies_purchase", e.target.value)} className="bg-[var(--bg-secondary)]" rows={4} /></div>
      </Card>

      <Card title="Mercado Pago" desc="A compra só é liberada quando a conta está conectada.">
        <div className={`rounded-lg p-3 text-sm ${settings?.mercadopago_connected ? "bg-green-500/10 text-green-400" : "bg-yellow-500/10 text-yellow-400"}`}>
          {settings?.mercadopago_connected ? "Conta conectada — pagamentos habilitados." : "Access Token não configurado. Pagamentos desabilitados."}
        </div>
        <Field label="Public Key (frontend)" k="mp_public_key" form={form} set={set} placeholder="APP_USR-..." hint="Chave pública do Mercado Pago (segura para o navegador). O Access Token privado é configurado nas variáveis de ambiente do servidor." />
      </Card>

      <div className="fixed bottom-0 left-0 right-0 lg:left-64 bg-[#13131A] border-t border-white/10 p-4 z-30">
        <div className="max-w-2xl mx-auto">
          <Button onClick={save} disabled={saving} data-testid="settings-save" className="btn-primary font-bold w-full h-11">
            {saving ? <Loader2 className="animate-spin mr-2" size={18} /> : <Save size={18} className="mr-2" />} Salvar tudo
          </Button>
        </div>
      </div>
    </div>
  );
}
